import { Injectable } from '@angular/core';
import * as fromViewer from '@app/client/viewer/reducers/viewer.reducer';
import * as fromRoot from '@app/reducers';
import { Campaign } from '@app/shared/models/campaign';
import { act, Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { switchMap, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { AdvertisingActions } from '../actions';
import { GoogleTagManagerService } from '../lib/google-tag-manager/angular-google-tag-manager.service';
import { LocalStorageService } from '../services/local-storage.service';


@Injectable()
export class AdvertisingEffects {

  loadCampaign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvertisingActions.LoadCampaign),
      withLatestFrom(
        this.store$.select(fromRoot.getCountryCode),
        this.store$.select(fromRoot.getSelectedLanguage),
        this.store$.select(fromRoot.getDevice),
        this.store$.select(fromRoot.getIsMainDomain),
      ),
      switchMap(([action, countryCode, selectedLanguage, device, isMainDomain]) => {
        const localStorageCountryCode = this.localStorageService.getItem(Constant.smashQueryParams.campaignCountryCode);
        const localStorageLanguage = this.localStorageService.getItem(Constant.smashQueryParams.campaignLanguage)?.toLowerCase();
        const localStorageCampaignId = this.localStorageService.getItem(Constant.smashQueryParams.campaignId);

        const campaignCountryCode = localStorageCountryCode || countryCode;
        const supportedLanguages = Object.keys(Constant.supportedLanguages);
        const campaignLanguage = supportedLanguages.includes(localStorageLanguage) ? localStorageLanguage : selectedLanguage;

        const campaignsFromCountry = Constant.campaigns[campaignCountryCode] || Constant.campaigns.OTHER;
        // tslint:disable-next-line: max-line-length
        const campaignsDisplayable = campaignsFromCountry.filter(campaign => canDisplayCampaign(campaign, action.target) && (campaign.status === 'Enabled' || campaign.status === undefined || campaign.name === localStorageCampaignId));

        if (localStorageCampaignId) {
          const filteredCampaigns = campaignsDisplayable.filter(campaign => campaign.name === localStorageCampaignId);
          // tslint:disable-next-line: max-line-length no-shadowed-variable
          const campaignSelected = filteredCampaigns.find(campaign => campaign.language === campaignLanguage.toUpperCase()) || filteredCampaigns.find(campaign => campaign.language === 'DEFAULT') || filteredCampaigns[0] || null;
          return [AdvertisingActions.LoadCampaignSuccess({ campaign: campaignSelected })];
        }

        if (!isMainDomain || campaignsDisplayable.length === 0) {
          return [AdvertisingActions.LoadCampaignSuccess({ campaign: null })];
        }

        let campaignSelected: Campaign | null = null;

        if (device?.isMobile) {
          const campaignMobile = campaignsDisplayable.filter(campaign => campaign.customizationMobile || campaign.promotionMobile);
          campaignSelected = campaignMobile[Math.floor(Math.random() * campaignMobile.length)] || null;
          if (campaignSelected) {
            campaignSelected = {
              ...campaignSelected,
              customization: campaignSelected.customizationMobile,
            };
          }
        } else {
          const { name } = campaignsDisplayable[Math.floor(Math.random() * campaignsDisplayable.length)];
          const campaignsGroupedByName = campaignsDisplayable.filter(campaign => campaign.name === name);
          // tslint:disable-next-line: max-line-length
          campaignSelected = campaignsGroupedByName.find(campaign => campaign.language === campaignLanguage.toUpperCase()) || campaignsGroupedByName.find(campaign => campaign.language === 'DEFAULT') || campaignsGroupedByName[0] || null;
        }

        if (campaignSelected) {
          this.googleTagManagerService.pushTag({
            smashCampaignName: campaignSelected.name,
            smashCampaignBackgroundType: campaignSelected.customization.background.type,
            smashCampaignBackgroundUrl: campaignSelected.customization.background.sourceId ?? campaignSelected.customization.background.sourceUrl,
            smashCampaignLogoUrl: campaignSelected.customization.logo.sourceUrl,
            smashCampaignLanguage: campaignSelected.language,
            smashCampaignTarget: campaignSelected.target,
          });
        }

        return [AdvertisingActions.LoadCampaignSuccess({ campaign: campaignSelected })];
      })
    )
  );

  constructor(
    private readonly store$: Store<fromRoot.State & fromViewer.State>,
    private googleTagManagerService: GoogleTagManagerService,
    private localStorageService: LocalStorageService,
    private actions$: Actions,
  ) { }

}

function canDisplayCampaign(campaign: Campaign, target: 'Uploader' | 'Viewer' | 'All'): boolean {
  if (campaign?.displayDays?.length > 0) {
    if (!campaign?.displayDays.find(day => day === moment().locale('en').format('dddd'))) {
      return false;
    }
  }
  if (campaign?.displayPeriods?.length) {
    const format = 'hh:mm:ss';
    return campaign.displayPeriods.some(period => {
      const startDate = moment(period.startHour, format);
      const endDate = moment(period.endHour, format);
      return moment().isBetween(startDate, endDate);
    });
  }
  if (campaign.target !== target && campaign.target !== 'All') {
    return false;
  }
  return true;
}
